import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  FaCalculator, FaFileInvoiceDollar, FaQuoteRight, FaChartLine,
  FaReceipt, FaCoins, FaChartPie, FaWallet,
} from "react-icons/fa6";
import { fetchSystems } from "../api";

// Deterministic pseudo-random scatter (avoids Math.random() re-shuffling icons on every render).
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

const BACKGROUND_ICONS = [FaCalculator, FaFileInvoiceDollar, FaQuoteRight, FaChartLine, FaReceipt, FaCoins, FaChartPie, FaWallet];

// Role 0 = front (zoomed in, on top), role 1 = middle, role 2 = back.
const roleStyles = [
  { x: 0, y: 0, scale: 1.08, opacity: 1, zIndex: 30 },
  { x: -55, y: 55, scale: 0.92, opacity: 0.65, zIndex: 20 },
  { x: -110, y: 110, scale: 0.82, opacity: 0.35, zIndex: 10 },
];

function SystemBlock({ system, reversed, seedOffset }) {
  const [order, setOrder] = useState([0, 1, 2]);

  useEffect(() => {
    const interval = setInterval(() => {
      setOrder((prev) => [prev[1], prev[2], prev[0]]);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const floatingIcons = useMemo(() => BACKGROUND_ICONS.map((Icon, i) => {
    const seed = i + seedOffset;
    return {
      Icon,
      top: `${Math.round(seededRandom(seed * 12.9898) * 90)}%`,
      left: `${Math.round(seededRandom(seed * 78.233) * 90)}%`,
      size: 22 + Math.round(seededRandom(seed * 37.719) * 16),
      duration: `${(16 + seededRandom(seed * 4.567) * 10).toFixed(1)}s`,
      delay: `${(seededRandom(seed * 91.345) * 4).toFixed(1)}s`,
    };
  }), [seedOffset]);

  const services = (system.services || "").split("\n").map((s) => s.trim()).filter(Boolean);
  // The image stack always has 3 layers: with fewer than 3 uploaded images the available ones are
  // reused, and with none at all (a text-only body section) the image column is left out entirely.
  const uploaded = [system.image_1, system.image_2, system.image_3].filter(Boolean);
  const images = uploaded.length ? [0, 1, 2].map((i) => uploaded[i % uploaded.length]) : [];
  const hasImages = images.length > 0;

  return (
    <section
      id={`system-${system.id}`}
      className="relative w-full py-24 scroll-mt-20 text-white overflow-hidden bg-navy border-b border-navy-border"
    >

      {/* Slowly drifting accounting icons */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {floatingIcons.map(({ Icon, top, left, size, duration, delay }, i) => (
          <Icon
            key={i}
            className="floating-light absolute text-accent-light/10"
            style={{ top, left, width: size, height: size, animationDuration: duration, animationDelay: delay }}
          />
        ))}
      </div>

      <div className="relative max-w-[1400px] mx-auto px-8">

        {/* Odd position: image left. Even position: image right (flex-row-reverse). */}
        <div className={`flex flex-col ${reversed ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-16`}>

          {/* Layered Images */}

          {hasImages && (
          <div className="flex-1 relative flex justify-center items-center">

            <div className="relative w-[500px] h-[500px]">

              {images.map((src, i) => {
                const role = order.indexOf(i);
                const roleStyle = roleStyles[role];
                return (
                  <motion.img
                    key={i}
                    src={src}
                    alt="system"
                    className={`absolute top-0 right-0 w-full rounded-xl shadow-xl ${
                      role === 0 ? "border-4 border-accent-light" : "border border-white/20"
                    }`}
                    initial={false}
                    animate={{
                      x: roleStyle.x,
                      y: roleStyle.y,
                      scale: roleStyle.scale,
                      opacity: roleStyle.opacity,
                      zIndex: roleStyle.zIndex,
                    }}
                    transition={{ duration: 1.1, ease: "easeInOut" }}
                  />
                );
              })}

              {/* Logo badge */}
              {system.logo && (
                <motion.div
                  className="absolute -top-32 left-1/2 w-[300px] rounded-2xl bg-white shadow-xl border border-white/20 flex items-center justify-center p-4 z-40"
                  initial={{ opacity: 0, scale: 0.8, x: "-50%" }}
                  whileInView={{ opacity: 1, scale: 1, x: "-50%" }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <img
                    src={system.logo}
                    alt="Logo"
                    className="w-full h-auto max-h-32 object-contain"
                  />
                </motion.div>
              )}

            </div>
          </div>
          )}

          {/* Content column */}

          <div className={`flex-1 ${hasImages ? "" : "max-w-4xl"}`}>

            {system.label && (
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-accent-light uppercase tracking-widest text-sm font-semibold"
              >
                {system.label}
              </motion.span>
            )}

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-4xl md:text-5xl font-bold mt-3"
            >
              {system.heading}
            </motion.h2>

            {system.paragraph1 && (
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="mt-6 text-gray-300 text-lg leading-relaxed [&_p]:mb-3 [&_strong]:text-accent-light [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: system.paragraph1 }}
              />
            )}

            {system.paragraph2 && (
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="mt-4 text-gray-300 [&_p]:mb-3 [&_strong]:text-accent-light [&_strong]:font-semibold"
                dangerouslySetInnerHTML={{ __html: system.paragraph2 }}
              />
            )}

            {services.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mt-8">
                {services.map((service, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition"
                  >
                    {service}
                  </motion.div>
                ))}
              </div>
            )}

            {system.button_text && (
              system.button_url ? (
                <motion.a
                  href={system.button_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-block mt-10 px-8 py-3 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20"
                >
                  {system.button_text}
                </motion.a>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-10 px-8 py-3 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20"
                >
                  {system.button_text}
                </motion.button>
              )
            )}

          </div>

        </div>

      </div>

    </section>
  );
}

export default function SystemsSection() {
  const [systems, setSystems] = useState([]);

  useEffect(() => {
    fetchSystems().then(setSystems).catch(() => {});
  }, []);

  if (systems.length === 0) return null;

  return (
    <>
      {systems.map((system, idx) => (
        <SystemBlock
          key={system.id}
          system={system}
          reversed={(idx + 1) % 2 === 0}
          seedOffset={idx * 100}
        />
      ))}
    </>
  );
}
